import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from './api';
import useAuth from '../hooks/useAuth';
import { useSessionManager } from '../context/SessionManagerContext';
import { useSocket } from '../context/SocketContext';
import { isSuper } from '../utils/canDo';

const POLL_INTERVAL_SEC = 120;

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtElapsed(sec) {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) return `${h}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
  return `${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
}
function fmtTime(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
}
function fmtDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}
function fmtShortDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
}
function elapsedSec(startStr) {
  if (!startStr) return 0;
  return Math.max(0, Math.floor((Date.now() - new Date(startStr).getTime()) / 1000));
}

// ── Avatar ────────────────────────────────────────────────────────────────────
function nameColor(name = '') {
  const colors = ['#6366f1','#8b5cf6','#ec4899','#f43f5e','#f97316','#0ea5e9','#10b981','#14b8a6'];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % colors.length;
  return colors[h];
}
function initials(name = '') {
  const p = name.trim().split(/\s+/);
  return p.length >= 2 ? (p[0][0] + p[1][0]).toUpperCase() : name.slice(0,2).toUpperCase() || '?';
}
function Avatar({ name, photo, size = 52 }) {
  const bg = nameColor(name);
  const base = {
    width: size, height: size, borderRadius: '50%',
    border: '2.5px solid rgba(255,255,255,0.18)',
    boxShadow: '0 3px 12px rgba(0,0,0,0.45)', flexShrink: 0,
  };
  if (photo) return <img src={photo} alt={name} style={{ ...base, objectFit: 'cover', display: 'block' }} onError={e => { e.target.style.display='none'; }} />;
  return (
    <div style={{
      ...base, display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: `linear-gradient(135deg,${bg}ee,${bg}88)`,
      color: '#fff', fontWeight: 800, fontSize: Math.round(size * 0.36), letterSpacing: 0.5,
    }}>
      {initials(name)}
    </div>
  );
}

// ── Exercise status for display ───────────────────────────────────────────────
function exStatus(ex, idx, activeIdx, isFinished) {
  if (ex.held)                    return { icon: '🔒', label: 'Hold',     color: '#f59e0b', bg: 'rgba(245,158,11,0.12)'  };
  if (ex.status === 'done')       return { icon: '✅', label: 'Done',     color: '#22c55e', bg: 'rgba(34,197,94,0.10)'   };
  if (ex.status === 'skipped')    return { icon: '⏭', label: 'Skipped',  color: '#64748b', bg: 'rgba(100,116,139,0.10)' };
  if (idx === activeIdx && !isFinished)
                                  return { icon: '▶', label: 'Active',   color: '#60a5fa', bg: 'rgba(96,165,250,0.12)'  };
  return                                 { icon: '○', label: 'Upcoming', color: '#94a3b8', bg: 'transparent'            };
}

// ── SessionDetailsPanel (expanded section inside card) ────────────────────────
function SessionDetailsPanel({ appt, localSession, isOwner, ownerUsername, socketRef, stop }) {
  const liveExs  = localSession?.exercises || appt.exercises || [];
  const liveIdx  = localSession?.activeIdx ?? appt.activeIdx ?? 0;
  const isFinished = localSession?.finishedAt != null;

  const [exList,    setExList]    = useState(() => liveExs.map(e => ({ ...e })));
  const [dirty,     setDirty]     = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [saveMsg,   setSaveMsg]   = useState('');
  const [holdForm,  setHoldForm]  = useState({}); // { [idx]: { restartDate } }
  const [addForm,   setAddForm]   = useState(null); // { name, duration, reps }
  const [comment,   setComment]   = useState('');
  const [showComment, setShowComment] = useState(false);

  // Keep synced with live data when not dirty
  useEffect(() => {
    if (!dirty) setExList(liveExs.map(e => ({ ...e })));
  }, [liveExs, dirty]); // eslint-disable-line

  const activeExercises = exList.filter(ex => !ex.held);
  const heldExercises   = exList.filter(ex => ex.held);

  const markDirty = (updated) => { setExList(updated); setDirty(true); };

  // ── Owner actions ──────────────────────────────────────────────────────────
  const holdExercise = (idx) => {
    const updated = exList.map((ex, i) => i !== idx ? ex : {
      ...ex,
      held: true,
      heldAt: new Date().toISOString(),
      heldBy: ownerUsername,
      heldRestartDate: holdForm[idx]?.restartDate || null,
    });
    setHoldForm(prev => { const n = { ...prev }; delete n[idx]; return n; });
    markDirty(updated);
  };

  const unholdExercise = (idx) => {
    const updated = exList.map((ex, i) => i !== idx ? ex : {
      ...ex, held: false, heldAt: null, heldRestartDate: null, heldBy: null,
    });
    markDirty(updated);
  };

  const updateExField = (idx, field, value) => {
    const updated = exList.map((ex, i) => i !== idx ? ex : { ...ex, [field]: value });
    markDirty(updated);
  };

  const deleteExercise = (idx) => {
    const updated = exList.filter((_, i) => i !== idx);
    markDirty(updated);
  };

  const addExercise = () => {
    if (!addForm?.name?.trim()) return;
    const newEx = {
      name: addForm.name.trim(),
      duration: Number(addForm.duration) || 30,
      repetition: Number(addForm.reps) || 1,
      pendingreps: Number(addForm.reps) || 1,
      status: 'notStarted',
      held: false,
      _localId: 'owner-' + Date.now(),
    };
    markDirty([...exList, newEx]);
    setAddForm(null);
  };

  const saveChanges = async () => {
    setSaving(true); setSaveMsg('');
    try {
      await api.post(
        `${process.env.REACT_APP_URL}/patients/consultation/patientsequences/updateremaining/${appt.patientCard}/owner_modified`,
        { reorderedExers: exList }
      );
      if (socketRef?.current?.connected) {
        socketRef.current.emit('session:owner_exercise_update', {
          targetTherapist: appt.username,
          patientCard:     appt.patientCard,
          exercises:       exList,
        });
      }
      setSaveMsg('Saved ✅');
      setDirty(false);
    } catch (e) {
      setSaveMsg('Save failed ⚠');
    } finally {
      setSaving(false);
    }
  };

  // ── Readings ───────────────────────────────────────────────────────────────
  const readings = appt.sessionFeedback && Object.keys(appt.sessionFeedback).length > 0
    ? Object.entries(appt.sessionFeedback)
    : null;

  // ── Session comment (display only — save to DB not implemented here) ────────
  const existingComment = appt.sessionComments || localSession?.appointment?.sessionComments || '';

  // ── Stats ──────────────────────────────────────────────────────────────────
  const totalActive   = activeExercises.length;
  const doneCount     = activeExercises.filter(e => e.status === 'done').length;
  const skippedCount  = activeExercises.filter(e => e.status === 'skipped').length;
  const upcomingCount = totalActive - doneCount - skippedCount;
  const heldCount     = heldExercises.length;

  const panelStyle = {
    marginTop: 10,
    background: 'rgba(255,255,255,0.035)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 12,
    padding: '12px 14px',
    fontSize: 12,
  };
  const sectionTitle = {
    fontSize: 10, fontWeight: 700, color: '#475569',
    letterSpacing: '0.06em', textTransform: 'uppercase',
    marginBottom: 6, marginTop: 10,
  };

  return (
    <div style={panelStyle} onClick={stop}>

      {/* Stats row */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 10, paddingBottom: 8, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        {[
          { label: 'Active', val: totalActive, color: '#94a3b8' },
          { label: 'Done',   val: doneCount,   color: '#22c55e' },
          { label: 'Skipped',val: skippedCount, color: '#64748b' },
          { label: 'To do',  val: upcomingCount,color: '#60a5fa' },
          heldCount > 0 && { label: 'Held', val: heldCount, color: '#f59e0b' },
        ].filter(Boolean).map(s => (
          <div key={s.label} style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 9.5, color: '#475569', letterSpacing: 0.4 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── Active exercises ── */}
      <div style={sectionTitle}>Exercises</div>
      {activeExercises.length === 0 && (
        <div style={{ color: '#475569', fontSize: 11, fontStyle: 'italic' }}>No active exercises</div>
      )}
      {activeExercises.map((ex) => {
        const realIdx = exList.indexOf(ex);
        const st = exStatus(ex, realIdx, liveIdx, isFinished);
        const isUpcoming = st.label === 'Upcoming';
        const showHoldForm = holdForm[realIdx] !== undefined;
        return (
          <div key={realIdx} style={{
            background: st.bg, borderRadius: 8, padding: '7px 8px',
            marginBottom: 5, border: `1px solid ${st.label === 'Active' ? 'rgba(96,165,250,0.25)' : 'transparent'}`,
          }}>
            {/* Exercise row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13 }}>{st.icon}</span>
              <span style={{ flex: 1, color: '#e2e8f0', fontWeight: 600, fontSize: 12, minWidth: 80 }}>
                {ex.name || 'Unnamed'}
              </span>
              {/* Editable duration/reps for upcoming (owner only) */}
              {isOwner && isUpcoming ? (
                <>
                  <input type="number" min="1"
                    value={ex.duration || ''}
                    onChange={e => updateExField(realIdx, 'duration', Number(e.target.value))}
                    style={{ width: 38, fontSize: 11, textAlign: 'center', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 4, color: '#e2e8f0', padding: '2px 3px' }}
                    title="Duration (s)"
                  />
                  <span style={{ color: '#475569', fontSize: 10 }}>s</span>
                  <input type="number" min="1"
                    value={ex.repetition || 1}
                    onChange={e => updateExField(realIdx, 'repetition', Number(e.target.value))}
                    style={{ width: 28, fontSize: 11, textAlign: 'center', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 4, color: '#e2e8f0', padding: '2px 3px' }}
                    title="Reps"
                  />
                  <span style={{ color: '#475569', fontSize: 10 }}>×</span>
                </>
              ) : (
                <span style={{ color: '#64748b', fontSize: 11 }}>
                  {ex.duration}s × {ex.repetition || 1}
                </span>
              )}
              {/* Owner action buttons — upcoming exercises only */}
              {isOwner && isUpcoming && (
                <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                  {/* Hold */}
                  <button
                    style={actionBtnStyle('#f59e0b')}
                    title="Put on hold"
                    onClick={() => setHoldForm(prev =>
                      prev[realIdx] !== undefined
                        ? (() => { const n = { ...prev }; delete n[realIdx]; return n; })()
                        : { ...prev, [realIdx]: { restartDate: '' } }
                    )}
                  >🔒</button>
                  {/* Delete */}
                  <button
                    style={actionBtnStyle('#ef4444')}
                    title="Remove exercise"
                    onClick={() => deleteExercise(realIdx)}
                  >🗑</button>
                </div>
              )}
            </div>

            {/* Per-exercise comment (if any) */}
            {(ex.remarks || ex.commentLog?.length > 0) && (
              <div style={{ marginTop: 4, fontSize: 10.5, color: '#94a3b8', fontStyle: 'italic', paddingLeft: 20 }}>
                💬 {ex.remarks || ex.commentLog?.[ex.commentLog.length - 1]?.text}
              </div>
            )}

            {/* Hold form */}
            {isOwner && showHoldForm && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6, paddingLeft: 20, flexWrap: 'wrap' }}>
                <span style={{ color: '#f59e0b', fontSize: 10.5, fontWeight: 600 }}>Stop date: {fmtShortDate(new Date().toISOString())}</span>
                <label style={{ fontSize: 10.5, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 4 }}>
                  Restart:
                  <input type="date"
                    value={holdForm[realIdx]?.restartDate || ''}
                    onChange={e => setHoldForm(prev => ({ ...prev, [realIdx]: { restartDate: e.target.value } }))}
                    style={{ fontSize: 10.5, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 4, color: '#e2e8f0', padding: '2px 4px' }}
                  />
                </label>
                <button onClick={() => holdExercise(realIdx)} style={actionBtnStyle('#f59e0b', true)}>
                  ✓ Confirm Hold
                </button>
              </div>
            )}
          </div>
        );
      })}

      {/* ── Held exercises at end ── */}
      {heldExercises.length > 0 && (
        <>
          <div style={sectionTitle}>🔒 On Hold — Not counted in session time</div>
          {heldExercises.map((ex) => {
            const realIdx = exList.indexOf(ex);
            return (
              <div key={realIdx} style={{
                background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.20)',
                borderRadius: 8, padding: '7px 10px', marginBottom: 5,
                opacity: 0.8,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13 }}>🔒</span>
                  <span style={{ flex: 1, color: '#fbbf24', fontWeight: 600, fontSize: 12 }}>{ex.name}</span>
                  <span style={{ fontSize: 10.5, color: '#92400e' }}>
                    Stop: {fmtShortDate(ex.heldAt)}
                  </span>
                  {ex.heldRestartDate && (
                    <span style={{ fontSize: 10.5, color: '#78350f' }}>
                      → Restart: {fmtShortDate(ex.heldRestartDate)}
                    </span>
                  )}
                  {isOwner && (
                    <button onClick={() => unholdExercise(realIdx)} style={actionBtnStyle('#22c55e')}>
                      ▶ Unhold
                    </button>
                  )}
                </div>
                {ex.heldBy && (
                  <div style={{ fontSize: 10, color: '#78350f', marginTop: 3, paddingLeft: 22 }}>
                    by {ex.heldBy}
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}

      {/* ── Add exercise form (owner only) ── */}
      {isOwner && (
        <div style={{ marginTop: 8 }}>
          {addForm ? (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', background: 'rgba(99,102,241,0.10)', border: '1px solid rgba(99,102,241,0.25)', borderRadius: 8, padding: '8px 10px' }}>
              <input placeholder="Exercise name"
                value={addForm.name} onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))}
                style={{ flex: '1 1 120px', ...inputStyle }}
              />
              <input type="number" min="1" placeholder="Dur (s)"
                value={addForm.duration} onChange={e => setAddForm(f => ({ ...f, duration: e.target.value }))}
                style={{ width: 60, ...inputStyle }}
              />
              <input type="number" min="1" placeholder="Reps"
                value={addForm.reps} onChange={e => setAddForm(f => ({ ...f, reps: e.target.value }))}
                style={{ width: 50, ...inputStyle }}
              />
              <button onClick={addExercise} style={actionBtnStyle('#6366f1', true)}>＋ Add</button>
              <button onClick={() => setAddForm(null)} style={{ ...actionBtnStyle('#475569'), fontSize: 11 }}>✕</button>
            </div>
          ) : (
            <button
              onClick={() => setAddForm({ name: '', duration: '30', reps: '1' })}
              style={{ fontSize: 11, color: '#6366f1', background: 'rgba(99,102,241,0.10)', border: '1px dashed rgba(99,102,241,0.35)', borderRadius: 7, padding: '5px 12px', cursor: 'pointer' }}
            >
              ＋ Add Exercise
            </button>
          )}
        </div>
      )}

      {/* ── Lab Readings ── */}
      <div style={sectionTitle}>Lab Readings</div>
      {readings ? (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {readings.map(([k, v]) => (
            <span key={k} style={{ fontSize: 11, background: 'rgba(14,165,233,0.12)', border: '1px solid rgba(14,165,233,0.22)', color: '#38bdf8', borderRadius: 6, padding: '2px 8px' }}>
              {k}: {typeof v === 'object' ? JSON.stringify(v) : v}
            </span>
          ))}
        </div>
      ) : (
        <div style={{ color: '#334155', fontSize: 11, fontStyle: 'italic' }}>No readings recorded yet</div>
      )}

      {/* ── Session Comments ── */}
      <div style={sectionTitle}>Session Comments</div>
      {existingComment
        ? <div style={{ fontSize: 11.5, color: '#94a3b8', lineHeight: 1.5, background: 'rgba(255,255,255,0.04)', borderRadius: 7, padding: '6px 9px' }}>{existingComment}</div>
        : <div style={{ color: '#334155', fontSize: 11, fontStyle: 'italic' }}>No comments yet</div>
      }

      {/* Owner comment input */}
      {isOwner && (
        <div style={{ marginTop: 8 }}>
          {showComment ? (
            <div style={{ display: 'flex', gap: 6, flexDirection: 'column' }}>
              <textarea rows="2" value={comment} onChange={e => setComment(e.target.value)}
                placeholder="Add an owner note (visible to therapist via socket)…"
                style={{ ...inputStyle, resize: 'none', width: '100%', boxSizing: 'border-box', fontSize: 11.5 }}
              />
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  style={actionBtnStyle('#6366f1', true)}
                  onClick={() => {
                    if (!comment.trim() || !socketRef?.current?.connected) return;
                    socketRef.current.emit('session:owner_exercise_update', {
                      targetTherapist: appt.username,
                      patientCard:     appt.patientCard,
                      exercises:       exList,
                      ownerNote:       comment.trim(),
                    });
                    setComment(''); setShowComment(false);
                    setSaveMsg('Note sent ✅');
                  }}
                >Send Note</button>
                <button onClick={() => { setShowComment(false); setComment(''); }} style={{ ...actionBtnStyle('#475569'), fontSize: 11 }}>✕</button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowComment(true)}
              style={{ fontSize: 11, color: '#94a3b8', background: 'rgba(255,255,255,0.04)', border: '1px dashed rgba(255,255,255,0.12)', borderRadius: 7, padding: '4px 12px', cursor: 'pointer' }}
            >
              ✏ Add Note for Therapist
            </button>
          )}
        </div>
      )}

      {/* ── Save bar ── */}
      {(dirty || saveMsg) && (
        <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end', paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          {saveMsg && <span style={{ fontSize: 11, color: saveMsg.includes('✅') ? '#22c55e' : '#f87171' }}>{saveMsg}</span>}
          {dirty && (
            <>
              <button onClick={() => { setExList(liveExs.map(e => ({ ...e }))); setDirty(false); setSaveMsg(''); }}
                style={{ ...actionBtnStyle('#475569'), fontSize: 11 }}>
                ↩ Reset
              </button>
              <button onClick={saveChanges} disabled={saving}
                style={actionBtnStyle('#6366f1', true)}>
                {saving ? '⏳ Saving…' : '💾 Save Changes'}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

const actionBtnStyle = (color, wide) => ({
  padding: wide ? '4px 10px' : '3px 6px',
  borderRadius: 6, border: 'none',
  background: `${color}33`,
  color, cursor: 'pointer',
  fontSize: 12, fontWeight: 600,
  flexShrink: 0,
});
const inputStyle = {
  background: 'rgba(255,255,255,0.08)',
  border: '1px solid rgba(255,255,255,0.15)',
  borderRadius: 5, color: '#e2e8f0',
  padding: '4px 6px', fontSize: 11.5,
};

// ── Session card ──────────────────────────────────────────────────────────────
function SessionCard({ appt, tick, onOpen, localSession, isOwner, ownerUsername, socketRef }) {
  const [expanded, setExpanded] = useState(false);

  const name      = appt.profileName || appt.name || appt.patientCard || 'Unknown';
  const startedAt = appt.actualStartTime || appt.start;

  const hasLocal  = localSession != null;
  const isFinished = hasLocal && !!localSession.finishedAt;
  const activeEx  = hasLocal && !isFinished ? (localSession.exercises || [])[localSession.activeIdx] : null;
  const elapsed   = hasLocal ? localSession.elapsedSec : elapsedSec(startedAt);

  const totalSessionSec = elapsedSec(startedAt);
  const hrs    = totalSessionSec / 3600;
  const isLong = hrs >= 1.5;
  const isVery = hrs >= 2;

  const stopPropagation = (e) => e.stopPropagation();

  return (
    <div
      onClick={() => !expanded && onOpen(appt)}
      style={{
        background:   isFinished
          ? 'linear-gradient(135deg,#1a1a00,#0f0f00)'
          : isVery
            ? 'linear-gradient(135deg,#3b1500,#1a0800)'
            : isLong
              ? 'linear-gradient(135deg,#1f2d1a,#0f1a0a)'
              : 'linear-gradient(135deg,#1e293b,#0f172a)',
        border:       `1.5px solid ${isFinished ? '#facc15' : isVery ? '#f97316' : isLong ? '#4ade80' : 'rgba(255,255,255,0.10)'}`,
        borderRadius: 18,
        padding:      '18px 20px',
        cursor:       expanded ? 'default' : 'pointer',
        transition:   'transform 0.15s, box-shadow 0.15s',
        boxShadow:    isVery
          ? '0 0 0 2px rgba(249,115,22,0.25), 0 10px 36px rgba(0,0,0,0.60)'
          : '0 10px 36px rgba(0,0,0,0.50)',
        userSelect:   'none',
      }}
      onMouseEnter={e => { if (!expanded) { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 16px 44px rgba(0,0,0,0.65)'; } }}
      onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = isVery ? '0 0 0 2px rgba(249,115,22,0.25), 0 10px 36px rgba(0,0,0,0.60)' : '0 10px 36px rgba(0,0,0,0.50)'; }}
    >
      {/* ── Avatar + name + therapist ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <Avatar name={name} photo={appt.patientPhoto} size={50} />
          <span style={{
            position: 'absolute', bottom: 2, right: 2,
            width: 11, height: 11, borderRadius: '50%',
            background: isFinished ? '#facc15' : '#22c55e',
            boxShadow: isFinished ? 'none' : '0 0 0 0 rgba(34,197,94,0.7)',
            animation: isFinished ? 'none' : 'lsmPulse 1.8s ease-out infinite',
            border: '2px solid #0f172a',
          }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: '#f1f5f9', fontWeight: 800, fontSize: 15.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.2 }}>
            {name}
          </div>
          <div style={{ color: '#64748b', fontSize: 11.5, marginTop: 3, display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ fontSize: 11 }}>👤</span>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{appt.username || '—'}</span>
          </div>
        </div>
        {isVery && (
          <div style={{ background: 'rgba(249,115,22,0.15)', border: '1px solid rgba(249,115,22,0.4)', borderRadius: 8, padding: '2px 8px', fontSize: 10, fontWeight: 700, color: '#f97316', flexShrink: 0, whiteSpace: 'nowrap' }}>
            ⚠ Long
          </div>
        )}
      </div>

      {/* ── Timer / status ── */}
      {isFinished ? (
        <div style={{ textAlign: 'center', marginBottom: 6 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: '#facc15', letterSpacing: 0.3, marginBottom: 4 }}>✅ Finished Therapy Session</div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 18, marginBottom: 6, flexWrap: 'wrap' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 9.5, color: '#64748b', letterSpacing: 0.5 }}>LAST EXERCISE AT</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#a3a300' }}>
                {new Date(localSession.finishedAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
              </div>
            </div>
            {startedAt && (
              <>
                <div style={{ width: 1, background: 'rgba(255,255,255,0.07)' }} />
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 9.5, color: '#64748b', letterSpacing: 0.5 }}>SESSION TOTAL</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#a3a300' }}>
                    {fmtElapsed(Math.floor((localSession.finishedAt - new Date(startedAt).getTime()) / 1000))}
                  </div>
                </div>
              </>
            )}
          </div>
          <div style={{ background: 'rgba(250,204,21,0.12)', border: '1px solid rgba(250,204,21,0.35)', borderRadius: 8, padding: '5px 10px', fontSize: 11, color: '#facc15', fontWeight: 700 }}>
            ⚠ Mark not over yet — awaiting therapist
          </div>
        </div>
      ) : (
        <>
          {hasLocal && activeEx && (
            <div style={{ fontSize: 10.5, color: '#94a3b8', textAlign: 'center', marginBottom: 3, fontWeight: 600 }}>
              {localSession.isPaused ? '⏸ Paused — ' : '▶ '}{activeEx.name || 'Exercise'}
            </div>
          )}
          {!hasLocal && (
            <div style={{ fontSize: 9.5, color: '#475569', textAlign: 'center', marginBottom: 3, letterSpacing: 0.5 }}>SESSION ELAPSED</div>
          )}
          <div style={{
            fontFamily: 'monospace', fontSize: 38, fontWeight: 900, textAlign: 'center',
            letterSpacing: 3,
            color: hasLocal && localSession.isPaused ? '#64748b' : isVery ? '#fb923c' : isLong ? '#a3e635' : '#4ade80',
            textShadow: isVery ? '0 0 20px rgba(249,115,22,0.50)' : isLong ? '0 0 16px rgba(163,230,53,0.40)' : '0 0 16px rgba(74,222,128,0.40)',
            marginBottom: 4,
          }}>
            {fmtElapsed(elapsed)}
          </div>
        </>
      )}

      {/* ── Meta row ── */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 9.5, color: '#475569', marginBottom: 2, letterSpacing: 0.5 }}>STARTED</div>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: '#94a3b8' }}>{fmtTime(startedAt)}</div>
        </div>
        <div style={{ width: 1, background: 'rgba(255,255,255,0.06)' }} />
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 9.5, color: '#475569', marginBottom: 2, letterSpacing: 0.5 }}>DATE</div>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: '#94a3b8' }}>{fmtDate(startedAt)}</div>
        </div>
        {appt.patientCard && (
          <>
            <div style={{ width: 1, background: 'rgba(255,255,255,0.06)' }} />
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 9.5, color: '#475569', marginBottom: 2, letterSpacing: 0.5 }}>CARD</div>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: '#94a3b8', fontFamily: 'monospace' }}>{appt.patientCard}</div>
            </div>
          </>
        )}
      </div>

      {/* ── Tags ── */}
      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 10 }}>
        {appt.stype && (
          <span style={{ background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.30)', borderRadius: 6, padding: '2px 8px', fontSize: 10.5, color: '#a5b4fc', fontWeight: 600 }}>
            {appt.stype}
          </span>
        )}
        {appt.isOnline && (
          <span style={{ background: 'rgba(14,165,233,0.12)', border: '1px solid rgba(14,165,233,0.25)', borderRadius: 6, padding: '2px 8px', fontSize: 10.5, color: '#38bdf8', fontWeight: 600 }}>
            🌐 Online
          </span>
        )}
        <span style={{
          background: isFinished ? 'rgba(250,204,21,0.12)' : 'rgba(34,197,94,0.10)',
          border: `1px solid ${isFinished ? 'rgba(250,204,21,0.35)' : 'rgba(34,197,94,0.25)'}`,
          borderRadius: 6, padding: '2px 8px', fontSize: 10.5,
          color: isFinished ? '#facc15' : '#4ade80', fontWeight: 600,
        }}>
          {isFinished ? '✅ Finished' : '● Live'}
        </span>
      </div>

      {/* ── Bottom action row ── */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }} onClick={stopPropagation}>
        {/* Session Details toggle */}
        <button
          onClick={() => setExpanded(v => !v)}
          style={{
            flex: 1,
            background: expanded ? 'rgba(99,102,241,0.15)' : 'rgba(255,255,255,0.04)',
            border: `1px solid ${expanded ? 'rgba(99,102,241,0.40)' : 'rgba(255,255,255,0.08)'}`,
            borderRadius: 8, padding: '7px 12px',
            fontSize: 11, color: expanded ? '#a5b4fc' : 'rgba(255,255,255,0.35)',
            cursor: 'pointer', fontWeight: 600,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
          }}
        >
          {expanded ? '▲ Hide Details' : '▼ Session Details'}
          {(appt.exercises?.filter(e => !e.held).length > 0 || localSession?.exercises?.length > 0) && (
            <span style={{ background: 'rgba(96,165,250,0.20)', color: '#60a5fa', borderRadius: 8, fontSize: 10, padding: '1px 6px' }}>
              {(localSession?.exercises || appt.exercises || []).filter(e => !e.held).length} ex
            </span>
          )}
        </button>

        {/* Open session button */}
        <button
          onClick={() => onOpen(appt)}
          style={{
            background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 8, padding: '7px 12px', fontSize: 11,
            color: 'rgba(255,255,255,0.40)', cursor: 'pointer',
          }}
          title="Open in SessionIpadView"
        >
          ↗ Open
        </button>
      </div>

      {/* ── Expanded details panel ── */}
      {expanded && (
        <SessionDetailsPanel
          appt={appt}
          localSession={localSession}
          isOwner={isOwner}
          ownerUsername={ownerUsername}
          socketRef={socketRef}
          stop={stopPropagation}
        />
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function LiveSessionsMonitor() {
  const navigate      = useNavigate();
  const sm            = useSessionManager();
  const { socketRef } = useSocket() || {};
  const { auth }      = useAuth();

  // Owner = only 'owner' role gets exercise controls; all supervisors see details panel
  const isOwner = isSuper(auth);
  const ownerUsername = auth?.username || '';

  const [sessions,    setSessions]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [lastFetched, setLastFetched] = useState(null);
  const [isLive,      setIsLive]      = useState(false);
  const [tick,        setTick]        = useState(0);
  const [error,       setError]       = useState(null);
  const [footerOpen,  setFooterOpen]  = useState(true);
  const tickRef = useRef(null);

  // Include exercises + state from socket payload so remote sessions show detail
  const mapSocketEntry = (s) => ({
    ...(s.appointment || {}),
    patientCard:  s.patientCard,
    name:         s.patientName,
    profileName:  s.patientName,
    patientPhoto: s.patientPhoto,
    username:     s.therapist,
    exercises:    s.exercises    || [],
    activeIdx:    s.activeIdx    ?? 0,
    isPaused:     s.isPaused     ?? false,
    elapsedSec:   s.elapsedSec   ?? 0,
  });

  const fetchSessions = useCallback(async () => {
    setError(null);
    try {
      const res = await api.get(`${process.env.REACT_APP_URL}/appointments/live`);
      setSessions(res.data || []);
      setLastFetched(new Date());
    } catch (e) {
      setError('Failed to load live sessions.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const sock = socketRef?.current;
    if (!sock || !sock.connected) {
      fetchSessions();
      const id = setInterval(fetchSessions, POLL_INTERVAL_SEC * 1000);
      return () => clearInterval(id);
    }
    const onLiveUpdate = (liveList) => {
      setSessions((liveList || []).map(mapSocketEntry));
      setLastFetched(new Date());
      setIsLive(true);
      setLoading(false);
    };
    sock.on('session:live_update', onLiveUpdate);
    sock.emit('session:request_live');
    // REST merge poll — adds DB-only sessions that socket doesn't know about
    const mergeFallback = async () => {
      try {
        const res = await api.get(`${process.env.REACT_APP_URL}/appointments/live`);
        const dbList = res.data || [];
        setSessions(prev => {
          const socketCards = new Set(prev.map(s => s.patientCard).filter(Boolean));
          const dbOnly = dbList.filter(s => s.patientCard && !socketCards.has(s.patientCard));
          return dbOnly.length > 0 ? [...prev, ...dbOnly] : prev;
        });
      } catch (e) { /* ignore */ }
    };
    const fallbackId = setInterval(mergeFallback, POLL_INTERVAL_SEC * 1000);
    return () => { sock.off('session:live_update', onLiveUpdate); clearInterval(fallbackId); };
  }, [socketRef]); // eslint-disable-line

  useEffect(() => {
    tickRef.current = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(tickRef.current);
  }, []);

  const openSession = (appt) => {
    if (appt.patientCard) navigate(`/SessionIpadView/${appt.patientCard}`);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg,#0a0f1e 0%,#0f172a 60%,#0a0f1e 100%)',
      padding: '24px 28px', boxSizing: 'border-box',
      fontFamily: '-apple-system,BlinkMacSystemFont,Segoe UI,sans-serif',
    }}>
      <style>{`
        @keyframes lsmPulse {
          0%   { box-shadow: 0 0 0 0 rgba(34,197,94,0.75); }
          70%  { box-shadow: 0 0 0 8px rgba(34,197,94,0); }
          100% { box-shadow: 0 0 0 0 rgba(34,197,94,0); }
        }
        @keyframes lsmSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 28, borderBottom: '1px solid rgba(255,255,255,0.07)', paddingBottom: 20 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: sessions.length ? '#22c55e' : '#475569', boxShadow: sessions.length ? '0 0 8px #22c55e' : 'none', flexShrink: 0, animation: sessions.length ? 'lsmPulse 1.8s ease-out infinite' : 'none' }} />
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 900, color: '#f1f5f9', letterSpacing: 0.5 }}>Live Session Monitor</h1>
            {sessions.length > 0 && (
              <div style={{ background: 'linear-gradient(90deg,#22c55e,#16a34a)', color: '#fff', borderRadius: 20, fontSize: 12, fontWeight: 800, padding: '2px 10px', boxShadow: '0 2px 8px rgba(34,197,94,0.35)' }}>
                {sessions.length} running
              </div>
            )}
            {isOwner && (
              <span style={{ fontSize: 10.5, color: '#f59e0b', background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.30)', borderRadius: 6, padding: '2px 8px', fontWeight: 600 }}>
                Owner Controls Enabled
              </span>
            )}
          </div>
          <div style={{ color: '#475569', fontSize: 12, marginTop: 5 }}>
            {lastFetched
              ? `${isLive ? '🟢 Live · Updated' : 'Last refreshed'} at ${lastFetched.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}`
              : 'Connecting…'}
          </div>
        </div>
        <button
          onClick={() => { setLoading(true); socketRef?.current?.connected ? socketRef.current.emit('session:request_live') : fetchSessions(); }}
          disabled={loading}
          style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, color: '#94a3b8', fontSize: 13, fontWeight: 600, padding: '8px 16px', cursor: loading ? 'default' : 'pointer', display: 'flex', alignItems: 'center', gap: 7, opacity: loading ? 0.5 : 1 }}
        >
          <span style={{ display: 'inline-block', animation: loading ? 'lsmSpin 0.8s linear infinite' : 'none' }}>↻</span>
          {loading ? 'Refreshing…' : 'Refresh Now'}
        </button>
      </div>

      {error && (
        <div style={{ background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.30)', borderRadius: 10, padding: '12px 16px', color: '#f87171', fontSize: 13, marginBottom: 20 }}>
          {error}
        </div>
      )}

      {!loading && !error && sessions.length === 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: '80px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 64, lineHeight: 1 }}>🌿</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#475569' }}>No active sessions</div>
          <div style={{ fontSize: 13, color: '#334155', maxWidth: 320 }}>Sessions will appear here in real-time as therapists start them.</div>
        </div>
      )}

      {sessions.length > 0 && (
        <>
          <div style={{ fontSize: 11.5, color: '#334155', marginBottom: 16, letterSpacing: 0.3 }}>
            Sorted by start time · longest running first · {sessions.length} session{sessions.length !== 1 ? 's' : ''} active
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(310px, 1fr))', gap: 20 }}>
            {sessions.map(appt => (
              <SessionCard
                key={appt._id || appt.patientCard}
                appt={appt}
                tick={tick}
                onOpen={openSession}
                localSession={appt.patientCard ? sm?.sessions?.[appt.patientCard] : null}
                isOwner={isOwner}
                ownerUsername={ownerUsername}
                socketRef={socketRef}
              />
            ))}
          </div>
          <div style={{ marginTop: 28, borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}
              onClick={() => setFooterOpen(f => !f)}>
              <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>{footerOpen ? '▾' : '▸'} Legend</span>
            </div>
            {footerOpen && (
              <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 11, color: '#334155', marginTop: 8 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><span style={{ color: '#4ade80', fontWeight: 700 }}>●</span> Normal (&lt; 90 min)</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><span style={{ color: '#a3e635', fontWeight: 700 }}>●</span> Long (90 min+)</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><span style={{ color: '#fb923c', fontWeight: 700 }}>●</span> ⚠ Very long (2 hr+)</span>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
